import React, { useState } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { useBuyerAuth } from '../../contexts/BuyerAuthContext';
import axios from 'axios';
import useSEO from '../../hooks/useSEO';
import { useMsg91Otp, isMsg91Configured } from '../../components/Msg91Otp';
import './BuyerAuth.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const BuyerLogin = () => {
  useSEO({
    title: 'Login to Buy Sell Taxi',
    description: 'Login to Buy Sell Taxi with your phone number or email to save vehicles, book test drives and submit enquiries.',
    url: 'https://www.buyselltaxi.com/buyer/login',
    noindex: true,
  });

  const { loginWithOtp, loginWithAccessToken } = useBuyerAuth();
  const { launch, ready, loading: widgetLoading, failed: widgetFailed } = useMsg91Otp();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const from = location.state?.from || '/';
  // ?type=dealer comes from the dealer CTA and has to survive the OTP round
  // trip, otherwise they land on the plain form and the intent is lost.
  const registerPath = searchParams.get('type') === 'dealer'
    ? '/buyer/register?type=dealer'
    : '/buyer/register';

  const [method, setMethod] = useState('phone'); // 'phone' | 'email'
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Falls back to the phone/email form when the widget is not configured, its
  // script is blocked, or the server has no auth key to verify tokens with.
  const [showFallback, setShowFallback] = useState(!isMsg91Configured());

  const finish = (buyerAccount) => {
    if (!buyerAccount.registration_complete) navigate(registerPath, { state: { from } });
    else if (searchParams.get('type') === 'dealer') navigate(registerPath, { state: { from } });
    else navigate(from);
  };

  const handleWidgetLogin = () => {
    setError('');
    launch({
      identifier: phone.replace(/[^\d]/g, '') || undefined,
      onSuccess: async (accessToken) => {
        setLoading(true);
        try {
          finish(await loginWithAccessToken(accessToken));
        } catch (err) {
          // 503 means the server cannot verify tokens — that is our problem, so
          // put the old form in front of them rather than a dead end.
          if (err.response?.status === 503) {
            setShowFallback(true);
            setError('OTP sign-in is unavailable right now. Use the form below.');
          } else {
            setError(err.response?.data?.error || 'Could not sign you in. Please try again.');
          }
        } finally {
          setLoading(false);
        }
      },
      onFailure: () => setError(''),  // the user closed the modal; not an error
    });
  };

  const handleSendOtp = async () => {
    setLoading(true);
    setError('');
    try {
      const payload = method === 'phone' ? { phone } : { email };
      await axios.post(`${API_URL}/buyer/send-otp`, payload);
      setOtpSent(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setLoading(true);
    setError('');
    try {
      const payload = method === 'phone' ? { phone, otp } : { email, otp };
      finish(await loginWithOtp(payload));
    } catch (err) {
      setError(err.response?.data?.error || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="buyer-auth-page" data-testid="buyer-login-page">
      <div className="buyer-auth-card">
        <div className="auth-logo">🚖</div>
        <h1>Buy Sell Taxi</h1>
        <p className="auth-subtitle">Login to submit enquiries and book test drives</p>

        {error && <div className="auth-error" data-testid="login-error">{error}</div>}

        {!widgetFailed && (
          <div className="otp-widget-block" data-testid="otp-widget-block">
            <button
              type="button"
              className="auth-submit otp-widget-btn"
              onClick={handleWidgetLogin}
              disabled={!ready || widgetLoading || loading}
              data-testid="otp-widget-btn"
            >
              {widgetLoading ? 'Preparing sign-in…' : 'Sign in with OTP'}
            </button>
            <p className="otp-widget-hint">We text a one-time code to your phone. No password needed.</p>
            {!showFallback && (
              <button type="button" className="resend-btn" onClick={() => setShowFallback(true)} data-testid="show-fallback">
                Use email instead
              </button>
            )}
          </div>
        )}

        {(showFallback || widgetFailed) && (
        <>
        {!widgetFailed && <div className="auth-divider"><span>or</span></div>}

        <div className="method-toggle" data-testid="method-toggle">
          <button className={`method-btn ${method === 'phone' ? 'active' : ''}`}
            onClick={() => { setMethod('phone'); setOtpSent(false); }}>
            Phone OTP
          </button>
          <button className={`method-btn ${method === 'email' ? 'active' : ''}`}
            onClick={() => { setMethod('email'); setOtpSent(false); }}>
            Email OTP
          </button>
        </div>

        {!otpSent ? (
          <div className="auth-form">
            {method === 'phone' ? (
              <div className="form-group">
                <label>Phone Number</label>
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="+91 XXXXX XXXXX" data-testid="phone-input" />
              </div>
            ) : (
              <div className="form-group">
                <label>Email Address</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com" data-testid="email-input" />
              </div>
            )}
            <button className="auth-submit" onClick={handleSendOtp} disabled={loading || (!phone && method === 'phone') || (!email && method === 'email')} data-testid="send-otp-btn">
              {loading ? 'Sending...' : 'Send OTP'}
            </button>
          </div>
        ) : (
          <div className="auth-form">
            <p className="otp-sent-msg">OTP sent to {method === 'phone' ? phone : email}</p>
            <div className="form-group">
              <label>Enter 6-digit OTP</label>
              <input type="text" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter OTP" maxLength={6} data-testid="otp-input" />
            </div>
            <button className="auth-submit" onClick={handleVerifyOtp}
              disabled={loading || otp.length !== 6} data-testid="verify-otp-btn">
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>
            <button className="resend-btn" onClick={() => setOtpSent(false)}>
              Change {method === 'phone' ? 'number' : 'email'} or resend OTP
            </button>
          </div>
        )}

        </>
        )}

        <div className="auth-footer-links">
          <Link to="/" data-testid="back-home">Back to Home</Link>
        </div>

        <div className="sell-cta-box" data-testid="sell-cta">
          <p>Want to sell your vehicle?</p>
          <Link to="/seller/register" className="sell-cta-link">Register as Seller &rarr;</Link>
        </div>
      </div>
    </div>
  );
};

export default BuyerLogin;
